import type { MaybeRefOrGetter } from 'vue';
import type { CalendarRange } from './build-calendar-range-filter';
import { Temporal } from 'temporal-polyfill';
import { computed, nextTick, shallowRef, toValue, watch } from 'vue';
import { getMonthWeeks } from '#layers/base/public';

interface UseYearGridNavigationOptions {
	root: MaybeRefOrGetter<HTMLElement | null>;
	range: MaybeRefOrGetter<CalendarRange>;
	focusedDate: MaybeRefOrGetter<Temporal.PlainDate>;
	/* Temporal’s dayOfWeek always numbers Monday 1 to Sunday 7 */
	weekStartsOn: MaybeRefOrGetter<1 | 7>;
}

/**
 * Moves Year's one tabbable day with the keyboard, within the range.
 *
 * Each day button needs its ISO date in `data-date`, inside `root`, and its `tabindex` from `activeKey`.
 */
export function useYearGridNavigation({ root, range, focusedDate, weekStartsOn }: UseYearGridNavigationOptions) {
	const activeDate = shallowRef(clampToRange(toValue(focusedDate)));
	const activeKey = computed(() => activeDate.value.toString());

	watch([() => toValue(focusedDate), () => toValue(range)], () => {
		activeDate.value = clampToRange(toValue(focusedDate));
	});

	function clampToRange(date: Temporal.PlainDate) {
		const { start, end } = toValue(range);
		if (Temporal.PlainDate.compare(date, start) < 0) return start;
		if (Temporal.PlainDate.compare(date, end) > 0) return end;
		return date;
	}

	function getDrawnRow(date: Temporal.PlainDate): Temporal.PlainDate[] {
		const row = getMonthWeeks({ month: date.toPlainYearMonth(), weekStartsOn: toValue(weekStartsOn) }).find((week) => week.some((day) => day?.equals(date)));
		return row?.filter((day): day is Temporal.PlainDate => day !== null) ?? [];
	}

	// Keeps Home and End within the month's days in the current row.
	function getKeyTarget(key: string, date: Temporal.PlainDate, isRtl: boolean): Temporal.PlainDate | undefined {
		switch (key) {
			case 'ArrowLeft':
				return isRtl ? date.add({ days: 1 }) : date.subtract({ days: 1 });
			case 'ArrowRight':
				return isRtl ? date.subtract({ days: 1 }) : date.add({ days: 1 });
			case 'ArrowUp':
				return date.subtract({ weeks: 1 });
			case 'ArrowDown':
				return date.add({ weeks: 1 });
			case 'Home':
				return getDrawnRow(date)[0];
			case 'End':
				return getDrawnRow(date).at(-1);
			case 'PageUp':
				return date.subtract({ months: 1 });
			case 'PageDown':
				return date.add({ months: 1 });
		}
	}

	async function onGridKeydown(event: KeyboardEvent) {
		// Leaves modifier key shortcuts to the browser.
		if (event.altKey || event.ctrlKey || event.metaKey) return;

		const day = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('[data-date]') : null;
		if (!day?.dataset.date) return;

		const date = Temporal.PlainDate.from(day.dataset.date);
		const target = getKeyTarget(event.key, date, getComputedStyle(day).direction === 'rtl');
		if (!target) return;

		// Keeps the browser from scrolling or paging on the same keys.
		event.preventDefault();
		activeDate.value = clampToRange(target);

		await nextTick();
		toValue(root)?.querySelector<HTMLElement>(`[data-date="${activeKey.value}"]`)?.focus();
	}

	return { activeKey, onGridKeydown };
}
